/**
 * Cached user-settings service. Deduplicates concurrent requests and caches results.
 * Prevents many components from each fetching /api/user-settings independently.
 */
import { fetchWithAuth } from "../utils/authFetch";

const cache = {};
const fetching = {};
const listeners = {};

// Notify subscribers whenever the cached settings for a sub change.
function notify(sub) {
  (listeners[sub] || []).forEach((cb) => cb(cache[sub] ?? null));
}

export function subscribeUserSettings(sub, cb) {
  if (!sub) return () => {};
  (listeners[sub] = listeners[sub] || []).push(cb);
  return () => {
    listeners[sub] = (listeners[sub] || []).filter((f) => f !== cb);
  };
}

export function getCachedUserSettings(sub) {
  if (!sub) return null;
  return cache[sub] ?? null;
}

export async function getUserSettings(sub) {
  if (!sub) return null;

  if (cache[sub]) return cache[sub];
  if (fetching[sub]) return fetching[sub];

  const p = (async () => {
    const res = await fetchWithAuth(`/api/user-settings/${encodeURIComponent(sub)}`);
    if (res.status === 404) {
      cache[sub] = null;
      return null;
    }
    if (!res.ok) throw new Error(`user-settings: ${res.status}`);
    const json = await res.json();
    cache[sub] = json;
    notify(sub);
    return json;
  })();

  fetching[sub] = p;
  try {
    return await p;
  } finally {
    delete fetching[sub];
  }
}

// Merge a patch into the cached settings synchronously (call BEFORE the POST) so pages mounting mid-save see the new state.
export function updateCachedUserSettings(sub, patch) {
  if (!sub || !(sub in cache)) return; // no known baseline — let the next fetch resolve it
  cache[sub] = { ...(cache[sub] || {}), ...patch };
  notify(sub);
}

export function invalidateUserSettingsCache(sub) {
  if (!sub) return;
  delete cache[sub];
  delete fetching[sub];
  notify(sub);
}
